import { Component, inject, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import {
  BehaviorSubject,
  combineLatest,
  filter,
  skip,
  switchMap,
  debounceTime,
} from 'rxjs'
//PRIMENG
import { TableLazyLoadEvent, TableModule } from 'primeng/table'
import { InputIconModule } from 'primeng/inputicon'
import { InputTextModule } from 'primeng/inputtext'
import { ButtonModule } from 'primeng/button'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { CardModule } from 'primeng/card'
import { TabsModule } from 'primeng/tabs'
import { DropdownModule } from 'primeng/dropdown'
import { ChipModule } from 'primeng/chip'
import { TagModule } from 'primeng/tag'
import { BadgeModule } from 'primeng/badge'
import { TooltipModule } from 'primeng/tooltip'
import { ToolbarModule } from 'primeng/toolbar'
import { SkeletonModule } from 'primeng/skeleton'
import { IconFieldModule } from 'primeng/iconfield'

//APP
import { MlSocieties } from '../../../../core/enums/ml-societies'
import { InterventionsService } from '../../services/interventions.service'
import { RouterLink, Router } from '@angular/router'
import { environment } from '../../../../../environments/environment'
import { Select } from 'primeng/select'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'app-interventions-to-reschedule',
  standalone: true,
  imports: [
    TableModule,
    CommonModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    ReactiveFormsModule,
    ButtonModule,
    ProgressSpinnerModule,
    CardModule,
    TabsModule,
    DropdownModule,
    ChipModule,
    TagModule,
    BadgeModule,
    TooltipModule,
    ToolbarModule,
    SkeletonModule,
    Select,
    FormsModule,
  ],
  templateUrl: './interventions-to-reschedule.component.html',
  styleUrl: './interventions-to-reschedule.component.scss',
})
export class InterventionsToRescheduleComponent implements OnInit {
  private interventionsService = inject(InterventionsService)
  private router = inject(Router)

  private tableLazyLoad$ = new BehaviorSubject<TableLazyLoadEvent | null>(null)
  private selectedMlSocietyId = new BehaviorSubject<string | null>(null)

  private allInterventions: any[] = []
  private filteredInterventions: any[] = []

  public allInterventionStatus: any[] = []
  public selectedInterventionStatus: string | null = null

  public allAssistants: any[] = []
  public selectedAssistant: string | null = null

  public allTechnicians: any[] = []
  public selectedTechnician: string | null = null
  public selectedStatus: string | null = null

  public filtersOpened: boolean = false

  public toRescheduleInterventions$ = new BehaviorSubject<any[]>([])
  public totalRecords$ = new BehaviorSubject<number>(0)
  public loading$ = this.interventionsService.toRescheduleLoading$

  private societyCounts: Record<string, number> = {}

  public sortOptions = [
    { label: 'Date (croissant)', value: 'startDate_asc' },
    { label: 'Date (décroissant)', value: 'startDate_desc' },
    { label: 'Nom (A-Z)', value: 'name_asc' },
    { label: 'Nom (Z-A)', value: 'name_desc' },
    { label: 'Client (A-Z)', value: 'customer_asc' },
    { label: 'Client (Z-A)', value: 'customer_desc' },
  ]

  mlSocieties = Object.values(MlSocieties).map((society) => {
    return {
      id: society,
      label: society,
      icon: 'tll tll-24 fa-logo-' + society,
    }
  })

  public searchInput = new FormControl('')
  public _activeTab: string = this.mlSocieties[0].id
  private initialized = false

  public activeTabIndex: number = 0
  private _previousTab: string | null = null

  private columnFilters: Record<string, string> = {}

  get activeTab() {
    return this._activeTab
  }

  set activeTab(value: string) {
    this.selectedMlSocietyId.next(value)
  }

  constructor() {
    this.selectedMlSocietyId
      .pipe(
        filter((society) => society !== null),
        debounceTime(50),
        takeUntilDestroyed(),
        switchMap((mlSociety) => {
          return this.interventionsService.getToReschedule({
            erpSource: mlSociety!,
          })
        })
      )
      .subscribe((response) => {
        if (response) {
          this.allInterventions = response
          this.societyCounts[this.selectedMlSocietyId.getValue()!] =
            response.length
          this.rebuildFiltersAndOptions()
          this.applyFilters()
        } else {
          console.error('Invalid response structure:', response)
        }
      })

    this.tableLazyLoad$
      .pipe(
        filter((event) => event !== null && this.initialized),
        debounceTime(50),
        takeUntilDestroyed()
      )
      .subscribe((event) => {
        this.applyFilters()
      })
  }

  ngOnInit() {
    this.initialized = true
    this.initializeData()
    this.setFiltersFromLocalStorage()
    this.loadAllSocietyCounts()
  }

  initializeData() {
    const defaultSociety = this.mlSocieties[0].id
    const defaultEvent: TableLazyLoadEvent = { first: 0, rows: 10 }

    this.tableLazyLoad$.next(defaultEvent)
    this.selectedMlSocietyId.next(defaultSociety)

    this.interventionsService
      .getToReschedule({
        erpSource: defaultSociety,
      })
      .subscribe((response) => {
        if (response) {
          this.allInterventions = response.map(
            (item: {
              userAssistant: {
                firstname: string
                lastname: string
                fullname: string
              }
              usersTechnicians: any[]
            }) => {
              const newItem = {
                techniciansString: '',
                ...item,
              }

              if (item.userAssistant) {
                const firstname = item.userAssistant.firstname || ''
                const lastname = item.userAssistant.lastname || ''
                newItem.userAssistant = {
                  ...item.userAssistant,
                  fullname: `${firstname} ${lastname}`,
                }
              }

              if (Array.isArray(item.usersTechnicians)) {
                newItem.techniciansString = item.usersTechnicians
                  .map((t) => `${t.firstname} ${t.lastname}`)
                  .join(', ')
              }

              return newItem
            }
          )

          this.rebuildFiltersAndOptions()
          this.filteredInterventions = [...this.allInterventions]
          this.totalRecords$.next(this.filteredInterventions.length)

          const rows = defaultEvent.rows || 10
          this.toRescheduleInterventions$.next(
            this.filteredInterventions.slice(0, rows)
          )
        } else {
          console.error('Invalid response format in initializeData:', response)
        }
      })

    this.mlSocieties.forEach((society) => {
      this.societyCounts[society.id] = 0
    })
  }

  private rebuildFiltersAndOptions() {
    // Clear current selections
    this.selectedInterventionStatus = null
    this.selectedAssistant = null
    this.selectedTechnician = null
    this.selectedStatus = null

    // Rebuild intervention status options
    this.allInterventionStatus = Array.from(
      new Set(this.allInterventions.map((item) => item.interventionStatus.name))
    ).map((type) => ({
      label: type,
      value: type,
    }))

    // Rebuild assistants list
    this.allAssistants = Array.from(
      new Set(
        this.allInterventions
          .filter((item) => item.userAssistant)
          .map(
            (item) =>
              `${item.userAssistant.firstname} ${item.userAssistant.lastname}`
          )
      )
    ).map((name) => ({
      label: name,
      value: name,
    }))

    // Rebuild technicians list
    const technicianNames = new Set<string>()
    this.allInterventions.forEach((item) => {
      if (Array.isArray(item.usersTechnicians)) {
        item.usersTechnicians.forEach((tech: any) => {
          technicianNames.add(`${tech.firstname} ${tech.lastname}`)
        })
      }
    })
    this.allTechnicians = Array.from(technicianNames).map((name) => ({
      label: name,
      value: name,
    }))
  }

  private setSelectOptions() {
    // Remove this method as it's replaced by rebuildFiltersAndOptions
  }

  protected applyFilters() {
    const event = this.tableLazyLoad$.getValue()
    if (!event) {
      return
    }

    let filtered = [...this.allInterventions]
    const searchTerm = this.searchInput.getRawValue()?.toLowerCase()

    if (searchTerm) {
      filtered = filtered.filter(
        (intervention) =>
          (intervention.name?.toLowerCase() || '').includes(searchTerm) ||
          (intervention.affairMlRef?.toLowerCase() || '').includes(
            searchTerm
          ) ||
          (
            intervention.affairFinalsCustomersFullNames?.toLowerCase() || ''
          ).includes(searchTerm) ||
          (intervention.affairWorkSiteLocality?.toLowerCase() || '').includes(
            searchTerm
          )
      )
    }

    if (this.selectedInterventionStatus) {
      filtered = filtered.filter(
        (item) =>
          item.interventionStatus.name === this.selectedInterventionStatus
      )

      localStorage.setItem(
        'toRescheduleInterventionStatus',
        this.selectedInterventionStatus
      )
    }

    if (this.selectedAssistant) {
      filtered = filtered.filter((item) => {
        if (!item.userAssistant) return false
        const fullname = `${item.userAssistant.firstname} ${item.userAssistant.lastname}`
        return fullname === this.selectedAssistant
      })

      localStorage.setItem('toRescheduleAssistant', this.selectedAssistant)
    }

    if (this.selectedTechnician) {
      filtered = filtered.filter((item) => {
        if (!Array.isArray(item.usersTechnicians)) return false
        return item.usersTechnicians.some((tech: any) => {
          const fullname = `${tech.firstname} ${tech.lastname}`
          return fullname === this.selectedTechnician
        })
      })

      localStorage.setItem('toRescheduleTechnician', this.selectedTechnician)
    }

    if (this.selectedStatus) {
      filtered = filtered.filter(
        (item) => item.interventionStatus.name === this.selectedStatus
      )
    }

    Object.entries(this.columnFilters).forEach(([field, value]) => {
      if (value) {
        const filterValue = value.toLowerCase()

        if (field === 'address') {
          filtered = filtered.filter((intervention) => {
            const postalCode = String(
              intervention.affairWorkSitePostalCode || ''
            ).toLowerCase()
            const locality = String(
              intervention.affairWorkSiteLocality || ''
            ).toLowerCase()
            return (
              postalCode.includes(filterValue) || locality.includes(filterValue)
            )
          })
        } else {
          filtered = filtered.filter((intervention) => {
            const fieldValue = this.getNestedValue(intervention, field)

            if (fieldValue === null || fieldValue === undefined) return false

            if (field === 'startDate' && fieldValue) {
              const dateStr = new Date(fieldValue).toLocaleDateString()
              return dateStr.includes(filterValue)
            }

            return String(fieldValue).toLowerCase().includes(filterValue)
          })
        }
      }
    })

    if (event.sortField) {
      filtered.sort((a, b) => {
        let sortField = Array.isArray(event.sortField)
          ? event.sortField[0]
          : event.sortField
        let valueA = this.getNestedValue(a, sortField!)
        let valueB = this.getNestedValue(b, sortField!)

        const result = valueA < valueB ? -1 : valueA > valueB ? 1 : 0
        return event.sortOrder === 1 ? result : -result
      })
    }

    this.totalRecords$.next(filtered.length)
    this.filteredInterventions = filtered

    const first = event.first || 0
    const rows = event.rows || 10
    const pagedData = filtered.slice(first, first + rows)

    this.toRescheduleInterventions$.next(pagedData)
  }

  private getNestedValue(obj: any, path: string): any {
    if (!path.includes('.')) return obj[path]
    return path
      .split('.')
      .reduce((prev, curr) => (prev ? prev[curr] : null), obj)
  }

  lazyLoadEmit(event: TableLazyLoadEvent) {
    if (this.initialized) {
      this.tableLazyLoad$.next(event)
      this.applyFilters()
    }
  }

  refreshData() {
    if (this.initialized) {
      this.tableLazyLoad$.next({ first: 0, rows: 10 })

      this.interventionsService
        .getToReschedule({
          erpSource: this.selectedMlSocietyId.getValue()!,
        })
        .subscribe((response) => {
          if (response) {
            this.allInterventions = response
            this.applyFilters()
          }
        })
    }
  }

  onSearchChange(event: Event) {
    if (this.initialized) {
      this.tableLazyLoad$.next({
        ...this.tableLazyLoad$.getValue(),
        first: 0,
      })

      this.applyFilters()
    }
  }

  onSortChange(event: any) {
    if (this.initialized) {
      this.tableLazyLoad$.next({
        ...this.tableLazyLoad$.getValue(),
        first: 0,
        sortField: event.value.split('_')[0],
        sortOrder: event.value.endsWith('asc') ? 1 : -1,
      })
      this.applyFilters()
    }
  }

  onColumnFilter(event: Event, field: string) {
    const inputElement = event.target as HTMLInputElement
    this.columnFilters[field] = inputElement.value

    if (this.initialized) {
      this.tableLazyLoad$.next({
        ...this.tableLazyLoad$.getValue(),
        first: 0,
      })
      this.applyFilters()
    }
  }

  clearAllFilters() {
    this.columnFilters = {}

    const filterInputs = document.querySelectorAll(
      '.p-column-filter'
    ) as NodeListOf<HTMLInputElement>
    filterInputs.forEach((input) => {
      input.value = ''
    })

    this.searchInput.setValue('')

    // Clear select filters
    this.selectedAssistant = null
    this.selectedTechnician = null
    this.selectedStatus = null
    this.selectedInterventionStatus = null

    this.tableLazyLoad$.next({
      ...this.tableLazyLoad$.getValue(),
      first: 0,
    })

    this.applyFilters()
  }

  navigateToIntervention(intervention: any) {
    const erpSource = intervention.erpSource
    if (erpSource === 'abm') {
      window.open(
        environment.abmUrl + 'interventions/' + intervention.id + '/edit',
        '_blank'
      )
    } else if (erpSource === 'nrj') {
      window.open(
        environment.nrjUrl + 'interventions/' + intervention.id + '/edit',
        '_blank'
      )
    }
  }

  viewInterventionDetails(intervention: any) {
    this.router.navigate([
      '/intervention',
      intervention.id,
      'view',
      intervention.erpSource,
    ])
  }

  rescheduleIntervention(intervention: any) {
    this.router.navigate([
      '/intervention',
      intervention.id,
      'reschedule',
      intervention.erpSource,
    ])
  }

  getStatusStyle(status: any): { [key: string]: string } {
    if (!status || !status.color) {
      return { color: 'var(--text-color)' }
    }
    return {
      color: status.color,
      fontWeight: 'bold',
    }
  }

  getStatusName(status: any): string {
    return status?.name || 'Non défini'
  }

  navigateToAffair(intervention: any) {
    window.open(`/affair/${intervention.affair.ml_ref}`, '_blank')
  }

  onActiveIndexChange(event: any) {
    if (!this.initialized) return

    const newTabId = this.mlSocieties[event].id

    if (this._previousTab !== newTabId) {
      this._previousTab = newTabId
      this._activeTab = newTabId

      this.selectedMlSocietyId.next(newTabId)
      this.tableLazyLoad$.next({ first: 0, rows: 10 })
    }
  }

  resetFilters() {
    this.selectedInterventionStatus = null
    this.selectedAssistant = null
    this.selectedTechnician = null
    this.selectedStatus = null

    localStorage.removeItem('toRescheduleInterventionStatus')
    localStorage.removeItem('toRescheduleAssistant')
    localStorage.removeItem('toRescheduleTechnician')

    this.applyFilters()
  }

  getTechniciansNames(usersTechnicians: any): string {
    return (
      usersTechnicians
        ?.map(
          (u: { firstname: any; lastname: any }) =>
            `${u.firstname} ${u.lastname}`
        )
        .join(', ') || ''
    )
  }

  setFiltersFromLocalStorage() {
    const interventionStatus = localStorage.getItem(
      'toRescheduleInterventionStatus'
    )
    const assistant = localStorage.getItem('toRescheduleAssistant')
    const technician = localStorage.getItem('toRescheduleTechnician')

    if (interventionStatus !== null) {
      this.selectedInterventionStatus = interventionStatus
      this.filtersOpened = true
    }

    if (assistant !== null) {
      this.selectedAssistant = assistant
      this.filtersOpened = true
    }

    if (technician !== null) {
      this.selectedTechnician = technician
      this.filtersOpened = true
    }

    this.applyFilters()
  }

  private loadAllSocietyCounts() {
    this.mlSocieties.forEach((society) => {
      if (society.id !== this.selectedMlSocietyId.getValue()) {
        this.interventionsService
          .getToReschedule({
            erpSource: society.id,
          })
          .subscribe((response) => {
            if (response) {
              this.societyCounts[society.id] = response.length
            }
          })
      }
    })
  }

  getSocietyCount(societyId: string): number {
    return this.societyCounts[societyId] || 0
  }
}
